import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Slot } from 'radix-ui';

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-brand-ring focus-visible:ring-[3px] focus-visible:ring-brand-ring/50 aria-invalid:border-brand-destructive aria-invalid:ring-brand-destructive/20 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'bg-brand-primary text-brand-primary-foreground [a&]:hover:bg-brand-primary/90',
        secondary: 'bg-brand-secondary text-brand-secondary-foreground [a&]:hover:bg-brand-secondary/90',
        destructive:
          'bg-brand-destructive text-white focus-visible:ring-brand-destructive/20 [a&]:hover:bg-brand-destructive/90',
        outline:
          'border-brand-border text-brand-foreground [a&]:hover:bg-brand-accent [a&]:hover:text-brand-accent-foreground',
        ghost: '[a&]:hover:bg-brand-accent [a&]:hover:text-brand-accent-foreground',
        link: 'text-brand-primary underline-offset-4 [a&]:hover:underline',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp data-slot='badge' data-variant={variant} className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
